'use client'
import * as React from 'react'
import dynamic from 'next/dynamic'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDataFor, type FormatData } from './format-data'
import { FORMATS, type FormatId } from './format-registry'
import { InViewMount } from './format-primitives'

// One lightweight skeleton for every engine while its chunk loads / before it
// scrolls into view. No bare spinner — a structured placeholder that matches
// the engine frame so there's no layout shift.
function EngineSkeleton() {
  return (
    <div className="grid min-h-[420px] animate-pulse motion-reduce:animate-none grid-cols-1 gap-px bg-border md:grid-cols-2">
      <div className="min-h-[260px] bg-secondary/60" />
      <div className="space-y-3 bg-card p-6">
        <div className="h-3 w-24 rounded bg-secondary" />
        <div className="h-7 w-3/4 rounded bg-secondary" />
        <div className="h-4 w-1/3 rounded bg-secondary" />
        <div className="h-20 w-full rounded bg-secondary" />
        <div className="h-11 w-40 rounded-[var(--r-pill)] bg-secondary" />
      </div>
    </div>
  )
}

// Dynamic-import each engine so only the ACTIVE one's JS is shipped/parsed.
// ssr:false keeps these interactive engines client-only (they're below the
// fold relative to the hero and gated behind InViewMount anyway).
const loading = () => <EngineSkeleton />
const ENGINES: Record<FormatId, React.ComponentType<{ data: FormatData }>> = {
  'shop-the-look': dynamic(() => import('./shop-the-look').then((m) => m.ShopTheLook), { ssr: false, loading }),
  'shoppable-story': dynamic(() => import('./shoppable-story').then((m) => m.ShoppableStory), { ssr: false, loading }),
  'social-gallery': dynamic(() => import('./social-gallery').then((m) => m.SocialGallery), { ssr: false, loading }),
  'interactive-pdp': dynamic(() => import('./interactive-pdp').then((m) => m.InteractivePdpHero), { ssr: false, loading }),
  lookbook: dynamic(() => import('./lookbook-carousel').then((m) => m.LookbookCarousel), { ssr: false, loading }),
  'before-after': dynamic(() => import('./before-after').then((m) => m.BeforeAfter), { ssr: false, loading }),
}

export interface IndustryOption {
  slug: string
  label: string
}

/**
 * The interactive gallery: pick an industry (real verticals), pick a format,
 * and the matching engine renders live with that industry's real demo data.
 * Only the active engine is mounted, and it's lazy-mounted on scroll.
 */
export function FormatGallery({ industries }: { industries: IndustryOption[] }) {
  const [slug, setSlug] = React.useState(industries[0]?.slug ?? '')
  const [format, setFormat] = React.useState<FormatId>('shop-the-look')

  // FormatData is derived from the registry on the client; cheap + memoised.
  const data = React.useMemo(() => formatDataFor(slug), [slug])
  const Engine = ENGINES[format]
  const activeIndustry = industries.find((i) => i.slug === slug)

  // A single-industry embed (industry/platform/widget surfaces) shouldn't show a
  // lone-button "industry" tablist or repeat the industry name the host section
  // already provides; only the multi-industry studio (/shoppable-video) does.
  const multiIndustry = industries.length > 1

  return (
    <div>
      {/* industry selector — multi-industry only */}
      {multiIndustry && (
        <div className="sticky top-16 z-30 -mx-4 mb-6 border-y bg-background/85 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <div
            className="flex gap-2 overflow-x-auto pb-0.5"
            role="tablist"
            aria-label="Choose an industry"
          >
            {industries.map((i) => (
              <button
                key={i.slug}
                type="button"
                role="tab"
                aria-selected={slug === i.slug}
                onClick={() => setSlug(i.slug)}
                className={cn(
                  'flex-none rounded-[var(--r-pill)] px-3.5 py-2 text-[13px] font-semibold transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                  'focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]',
                  slug === i.slug
                    ? 'bg-[var(--accent)] text-[var(--fg-on-accent)]'
                    : 'bg-secondary text-muted-foreground hover:bg-secondary/70 hover:text-foreground',
                )}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* heading + format chips */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl tracking-tight sm:text-3xl">
            {multiIndustry ? activeIndustry?.label : 'Pick a format'}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {multiIndustry
              ? 'Each format below renders live with this industry’s real demo content.'
              : `Every format renders live with real ${activeIndustry?.label ?? ''} demo content.`}
          </p>
        </div>
        <div className="flex gap-2 overflow-x-auto" role="tablist" aria-label="Choose a format">
          {FORMATS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={format === f.id}
              onClick={() => setFormat(f.id)}
              className={cn(
                'flex-none rounded-[var(--r-pill)] border px-3 py-1.5 text-xs font-semibold transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
                'focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]',
                format === f.id
                  ? 'border-[var(--accent)] text-[var(--accent)]'
                  : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
              )}
            >
              {f.name}
            </button>
          ))}
        </div>
      </div>

      {/* the live engine */}
      <InViewMount
        key={`${slug}-${format}`}
        placeholder={<EngineSkeleton />}
        className="overflow-hidden rounded-[var(--r-card)] border bg-card shadow-[var(--shadow-md)]"
      >
        {data ? <Engine data={data} /> : <EngineSkeleton />}
      </InViewMount>

      {/* what this format is + where it fits */}
      <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
        <ArrowUpRight className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden />
        <span>
          <span className="font-medium text-foreground">{FORMATS.find((f) => f.id === format)?.name}.</span>{' '}
          {FORMATS.find((f) => f.id === format)?.blurb}{' '}
          <span className="text-muted-foreground/80">Where it fits: {FORMATS.find((f) => f.id === format)?.surface}.</span>
        </span>
      </p>
    </div>
  )
}
